import React, { useEffect, useState } from "react";
import { X, Pencil, Trash2, Merge, RefreshCw, Tag as TagIcon, Check } from "lucide-react";
import { toast } from "sonner";
import { api, extractErrorMessage } from "@/lib/api";

/**
 * Admin Tag Manager — lists every distinct tag used across prim rows with its
 * occurrence count, supporting rename, delete and multi-source merge.
 *
 * Backend contract (admin-only writes):
 *   GET  /prim/tags                            → { rows: [{tag, count}], total }
 *   POST /prim/tags/rename  {old, new}         → bulk replace
 *   POST /prim/tags/delete  {tag}              → bulk pull
 *   POST /prim/tags/merge   {sources[], target}→ merge several into one
 */
export default function TagManagerModal({ onClose, onChanged }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [renaming, setRenaming] = useState({}); // {tag: {newName}}
  const [selected, setSelected] = useState(new Set());
  const [mergeTarget, setMergeTarget] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/prim/tags");
      setRows(r.data?.rows || []);
    } catch (e) {
      toast.error(extractErrorMessage(e, "Yüklenemedi"));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const notifyChanged = () => { onChanged && onChanged(); };

  const toggleSelect = (tag) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(tag)) next.delete(tag); else next.add(tag);
      return next;
    });
  };

  const startRename = (tag) => setRenaming({ ...renaming, [tag]: { newName: tag } });
  const cancelRename = (tag) => {
    const next = { ...renaming };
    delete next[tag];
    setRenaming(next);
  };
  const saveRename = async (tag) => {
    const r = renaming[tag];
    const newName = (r?.newName || "").trim();
    if (!newName) return toast.error("Yeni ad boş olamaz");
    if (newName === tag) { cancelRename(tag); return; }
    try {
      const resp = await api.post("/prim/tags/rename", { old: tag, new: newName });
      toast.success(`${resp.data?.updated || 0} prim güncellendi`);
      cancelRename(tag);
      await load();
      notifyChanged();
    } catch (e) {
      toast.error(extractErrorMessage(e, "Yeniden adlandırılamadı"));
    }
  };

  const removeTag = async (tag) => {
    if (!window.confirm(`"${tag}" etiketi tüm prim satırlarından silinecek. Devam?`)) return;
    try {
      const r = await api.post("/prim/tags/delete", { tag });
      toast.success(`${r.data?.updated || 0} prim güncellendi`);
      setSelected((prev) => { const n = new Set(prev); n.delete(tag); return n; });
      await load();
      notifyChanged();
    } catch (e) {
      toast.error(extractErrorMessage(e, "Silinemedi"));
    }
  };

  const doMerge = async () => {
    const sources = Array.from(selected);
    const target = mergeTarget.trim();
    if (sources.length < 2) return toast.error("En az 2 etiket seçin");
    if (!target) return toast.error("Hedef etiket adı girin");
    if (!window.confirm(`${sources.length} etiket → "${target}" olarak birleştirilecek. Devam?`)) return;
    try {
      const r = await api.post("/prim/tags/merge", { sources, target });
      toast.success(`${r.data?.updated || 0} prim güncellendi`);
      setSelected(new Set());
      setMergeTarget("");
      await load();
      notifyChanged();
    } catch (e) {
      toast.error(extractErrorMessage(e, "Birleştirilemedi"));
    }
  };

  const filtered = rows.filter((r) =>
    !search.trim() || r.tag.toLocaleLowerCase("tr").includes(search.toLocaleLowerCase("tr"))
  );
  const totalPrimTouched = rows.reduce((a, r) => a + r.count, 0);

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm grid place-items-center p-4" data-testid="tag-manager-modal">
      <div className="bg-white rounded-xl shadow-xl border border-slate-200 w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-sky-50 border border-sky-200 grid place-items-center">
              <TagIcon size={18} className="text-sky-600" />
            </div>
            <div>
              <h3 className="font-display text-lg font-bold text-slate-900">Etiket Yöneticisi</h3>
              <p className="text-xs text-slate-500 mt-0.5">
                Tüm prim etiketlerini listele, mükerrerleri birleştir, yanlış yazılanları yeniden adlandır veya kaldır.
              </p>
            </div>
          </div>
          <button onClick={onClose} data-testid="tag-manager-close" className="w-8 h-8 rounded-lg hover:bg-slate-100 grid place-items-center">
            <X size={16} />
          </button>
        </div>

        <div className="px-5 py-3 border-b border-slate-200 bg-slate-50 flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 bg-sky-100 text-sky-700 rounded">
              {rows.length} etiket · {totalPrimTouched} atama
            </span>
            <input
              data-testid="tag-manager-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Etiket ara..."
              className="text-xs px-2 py-1 border border-slate-300 rounded"
            />
            <button
              onClick={load}
              disabled={loading}
              data-testid="tag-manager-refresh"
              className="inline-flex items-center gap-1 text-xs px-2 py-1 bg-white border border-slate-300 rounded hover:bg-slate-50 disabled:opacity-50"
            >
              <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Yenile
            </button>
          </div>
          {selected.size >= 2 && (
            <div className="flex items-center gap-1.5" data-testid="tag-manager-merge-controls">
              <Merge size={14} className="text-indigo-600" />
              <input
                data-testid="tag-manager-merge-target"
                value={mergeTarget}
                onChange={(e) => setMergeTarget(e.target.value)}
                placeholder="Hedef etiket adı"
                className="text-xs px-2 py-1 border border-indigo-300 rounded"
              />
              <button
                onClick={doMerge}
                data-testid="tag-manager-merge-btn"
                className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1 bg-indigo-600 hover:bg-indigo-700 text-white rounded"
              >
                {selected.size} Etiketi Birleştir
              </button>
            </div>
          )}
        </div>

        <div className="flex-1 overflow-auto p-5">
          {loading && <div className="py-10 text-center text-slate-400 text-sm">Yükleniyor...</div>}
          {!loading && rows.length === 0 && (
            <div className="py-12 text-center text-slate-400 text-sm">Hiç etiket atanmamış</div>
          )}
          {!loading && rows.length > 0 && (
            <table className="w-full text-sm border border-slate-200 rounded-lg overflow-hidden" data-testid="tag-manager-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left text-xs text-slate-600">
                  <th className="px-3 py-2 w-10"></th>
                  <th className="px-3 py-2 font-bold">Etiket</th>
                  <th className="px-3 py-2 font-bold text-right">Prim Sayısı</th>
                  <th className="px-3 py-2 font-bold text-right">İşlem</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => {
                  const r2 = renaming[r.tag];
                  const isSel = selected.has(r.tag);
                  return (
                    <tr key={r.tag} className="border-t border-slate-100 hover:bg-slate-50">
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          checked={isSel}
                          onChange={() => toggleSelect(r.tag)}
                          data-testid={`tag-select-${r.tag}`}
                        />
                      </td>
                      <td className="px-3 py-2">
                        {r2 ? (
                          <div className="flex items-center gap-1">
                            <input
                              autoFocus
                              value={r2.newName}
                              onChange={(e) => setRenaming({ ...renaming, [r.tag]: { newName: e.target.value } })}
                              className="text-sm px-2 py-1 border border-slate-300 rounded font-semibold"
                              data-testid={`tag-rename-input-${r.tag}`}
                            />
                            <button onClick={() => saveRename(r.tag)} className="w-7 h-7 rounded bg-emerald-600 hover:bg-emerald-700 text-white inline-flex items-center justify-center" data-testid={`tag-rename-save-${r.tag}`}><Check size={14} /></button>
                            <button onClick={() => cancelRename(r.tag)} className="w-7 h-7 rounded border border-slate-200 text-slate-500 inline-flex items-center justify-center"><X size={14} /></button>
                          </div>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-[12px] font-semibold px-2 py-0.5 rounded-full bg-sky-100 text-sky-700 border border-sky-200">
                            # {r.tag}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right font-mono-tab text-slate-700 font-bold">{r.count}</td>
                      <td className="px-3 py-2 text-right">
                        {!r2 && (
                          <div className="inline-flex gap-1">
                            <button onClick={() => startRename(r.tag)} className="w-7 h-7 rounded text-slate-600 hover:bg-slate-100 inline-flex items-center justify-center" title="Yeniden adlandır" data-testid={`tag-rename-${r.tag}`}>
                              <Pencil size={13} />
                            </button>
                            <button onClick={() => removeTag(r.tag)} className="w-7 h-7 rounded text-rose-600 hover:bg-rose-50 inline-flex items-center justify-center" title="Sil" data-testid={`tag-delete-${r.tag}`}>
                              <Trash2 size={13} />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {filtered.length === 0 && (
                  <tr><td colSpan={4} className="px-3 py-6 text-center text-slate-400 text-xs">Aramaya uyan etiket yok</td></tr>
                )}
              </tbody>
            </table>
          )}
          {selected.size >= 1 && selected.size < 2 && (
            <div className="mt-3 text-[11px] text-slate-500">Birleştirme için en az 2 etiket seçmelisiniz.</div>
          )}
        </div>
      </div>
    </div>
  );
}
